import type { ReactNode } from 'react';
import { CircleOff, Loader2, Lock } from 'lucide-react';

import { LongArrow } from '@/components/brand/long-arrow';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { useI18nContext } from '@/i18n/i18n-react';
import {
  getFeatureGlimpseOverlay,
  isFeatureGlimpseDimmed,
  isFeatureOpenable,
  type AppFeatureState,
  type FeatureGlimpseOverlay,
} from '@/lib/app-editor/app-hub';
import { cn } from '@/lib/utils';

export type AppFeatureCardLayout = 'tile' | 'row';

export interface AppFeatureCardProps {
  /** "01" … "04". */
  number: string;
  /** Where the feature shows up: "No app", "No seu site"… */
  where: string;
  title: string;
  state: AppFeatureState;
  /** Already translated by the page. */
  summary: string;
  /** Visible explanation (waitingForUpdate), never a tooltip. */
  explanation?: string;
  /** Where the feature shows up, drawn (FeatureGlimpses). */
  glimpse: ReactNode;
  /** Desktop tile (a grid of four) or mobile row (stacked). */
  layout: AppFeatureCardLayout;
  isPending: boolean;
  onToggle: () => void;
  onOpen?: () => void;
  onRetry?: () => void;
}

/** The switch is 24px tall: on mobile its tap area grows to 44px. */
const SWITCH_HIT_AREA =
  "relative max-md:after:absolute max-md:after:-inset-3 max-md:after:content-['']";
const CHIP =
  'inline-flex h-[22px] w-fit items-center gap-1.5 rounded-full px-[9px] text-[10.5px] font-bold uppercase tracking-[0.12em] max-md:h-5 max-md:px-2 max-md:text-[10px]';

function useOverlayLabel(): Record<FeatureGlimpseOverlay, string> {
  const { LL } = useI18nContext();
  return {
    off: LL.appHub.glimpse.off(),
    notConfigured: LL.appHub.glimpse.notConfigured(),
    waitingForUpdate: LL.appHub.waitingForUpdate(),
    needsIntegration: LL.appHub.needsIntegration(),
  };
}

/** The state chip; on a tile the overlay already names waiting/integration. */
function StateChip({
  state,
  layout,
}: {
  state: AppFeatureState;
  layout: AppFeatureCardLayout;
}) {
  const { LL } = useI18nContext();
  const muted = 'bg-[#15191E]/[.06] text-[#4B5361]';

  switch (state.kind) {
    case 'configured':
      return state.active ? (
        <span className={cn(CHIP, 'bg-[#155DFC]/10 text-[#0B4AE0]')}>
          <span aria-hidden className="size-1.5 rounded-full bg-[#155DFC]" />
          {LL.appHub.active()}
        </span>
      ) : (
        <span className={cn(CHIP, muted)}>
          <span
            aria-hidden
            className="size-1.5 rounded-full border-[1.5px] border-[#5B6371]"
          />
          {LL.appHub.off()}
        </span>
      );
    case 'notConfigured':
      return (
        <span
          className={cn(
            CHIP,
            'text-[#4B5361] shadow-[inset_0_0_0_1px_rgba(21,25,30,0.18)]'
          )}
        >
          {LL.appHub.notConfigured()}
        </span>
      );
    case 'waitingForUpdate':
      return layout === 'row' ? (
        <span className={cn(CHIP, muted)}>{LL.appHub.waitingForUpdate()}</span>
      ) : null;
    case 'needsIntegration':
      return layout === 'row' ? (
        <span className={cn(CHIP, muted)}>
          <Lock aria-hidden strokeWidth={2.2} className="size-2.5" />
          {LL.appHub.needsIntegration()}
        </span>
      ) : null;
    case 'comingSoon':
      return (
        <span className={cn(CHIP, 'bg-white text-[#15191E]')}>
          {LL.appHub.comingSoon()}
        </span>
      );
    default:
      return null;
  }
}

function GlimpseOverlay({
  overlay,
  layout,
}: {
  overlay: FeatureGlimpseOverlay;
  layout: AppFeatureCardLayout;
}) {
  const label = useOverlayLabel()[overlay];
  const Icon = overlay === 'needsIntegration' ? Lock : CircleOff;

  if (layout === 'row') {
    // The chip beside it names the state.
    return (
      <span
        aria-hidden
        className="absolute left-1/2 top-1/2 flex size-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-[#15191E] text-white"
      >
        <Icon strokeWidth={1.7} className="size-3.5" />
      </span>
    );
  }
  return (
    <span className="absolute inset-0 flex items-center justify-center bg-[#F9F9F9]/35">
      <span className="flex h-7 items-center gap-[7px] rounded-full bg-[#15191E] pl-2.5 pr-3 text-xs font-semibold text-white shadow-[0_8px_20px_-6px_rgba(0,0,0,0.4)]">
        <Icon aria-hidden strokeWidth={1.7} className="size-[13px]" />
        {label}
      </span>
    </span>
  );
}

/**
 * One feature of the "Seu app" hub: a thumbnail of where it shows up, its
 * state and summary, and the quick on/off. The text and thumbnail open the
 * editor; the switch never sits inside that button.
 */
export function AppFeatureCard({
  number,
  where,
  title,
  state,
  summary,
  explanation,
  glimpse,
  layout,
  isPending,
  onToggle,
  onOpen,
  onRetry,
}: AppFeatureCardProps) {
  const { LL } = useI18nContext();
  const open = isFeatureOpenable(state) ? onOpen : undefined;
  const overlay = getFeatureGlimpseOverlay(state);
  const dark = state.kind === 'comingSoon';
  const tile = layout === 'tile';

  const thumbnail = (
    <span
      className={cn(
        'relative block shrink-0 overflow-hidden',
        tile ? 'h-[168px] w-full' : 'w-[104px] self-stretch'
      )}
    >
      <span
        aria-hidden
        className={cn(
          'absolute inset-0 transition-transform duration-1000 ease-[cubic-bezier(.2,.8,.2,1)] motion-reduce:transition-none',
          tile && open && 'group-hover:scale-[1.04]',
          isFeatureGlimpseDimmed(state) && 'opacity-[.55] grayscale-[0.85]'
        )}
      >
        {state.kind === 'loading' ? (
          <Skeleton data-skeleton className="absolute inset-0 rounded-none" />
        ) : (
          glimpse
        )}
      </span>
      {overlay && <GlimpseOverlay overlay={overlay} layout={layout} />}
    </span>
  );

  const text = (
    <span
      className={cn(
        'flex min-w-0 flex-col gap-2',
        tile
          ? 'min-h-[108px] px-[18px] pb-3.5 pt-[18px]'
          : 'flex-1 py-3.5 pl-3.5 pr-1 gap-1.5'
      )}
    >
      {!tile && (
        <span
          className={cn(
            'text-[10px] font-semibold uppercase tracking-[0.16em]',
            dark ? 'text-white/80' : 'text-[#155DFC]'
          )}
        >
          {number} · {where}
        </span>
      )}
      <span
        className={cn(
          'font-medium leading-tight tracking-[-0.015em]',
          tile ? 'text-lg' : 'text-base'
        )}
      >
        {title}
      </span>
      <StateChip state={state} layout={layout} />
      {state.kind === 'loading' ? (
        <Skeleton data-skeleton className="h-3 w-32" />
      ) : (
        (summary || state.kind === 'error') && (
          <span
            className={cn(
              'text-[13.5px] leading-snug max-md:text-[13px]',
              dark ? 'text-white/[.78]' : 'text-[#4B5361]'
            )}
          >
            {state.kind === 'error' ? LL.appHub.loadError() : summary}
          </span>
        )
      )}
      {explanation && (
        <span className="text-[13px] leading-normal text-[#4B5361]">
          {explanation}
        </span>
      )}
    </span>
  );

  const body = (
    <>
      {thumbnail}
      {text}
    </>
  );
  const bodyClass = cn(
    'flex min-w-0 text-left',
    tile ? 'flex-col' : 'flex-1 items-stretch'
  );

  return (
    <article
      data-feature-state={state.kind}
      className={cn(
        'group relative flex overflow-hidden',
        tile
          ? 'flex-col rounded-[22px] transition-[transform,box-shadow] duration-500 ease-[cubic-bezier(.2,.8,.2,1)] motion-reduce:transition-none'
          : 'min-h-[132px] items-stretch rounded-[18px]',
        tile &&
          open &&
          'hover:-translate-y-1.5 hover:shadow-[0_30px_60px_-30px_rgba(11,52,140,0.45),0_0_0_1px_rgba(21,25,30,0.06)]',
        dark
          ? 'bg-[#15191E] text-white shadow-[0_24px_40px_-28px_rgba(11,52,140,0.55)]'
          : 'bg-white text-[#15191E] shadow-[0_1px_2px_rgba(15,23,42,0.04),0_0_0_1px_rgba(21,25,30,0.07)]'
      )}
    >
      {tile && (
        <div
          className={cn(
            'flex h-10 shrink-0 border-b text-[10.5px] font-semibold uppercase tracking-[0.16em]',
            dark
              ? 'border-white/[.12] text-white/[.86]'
              : 'border-[#15191E]/[.08] text-[#4B5361]'
          )}
        >
          <span
            className={cn(
              'flex w-[52px] shrink-0 items-center justify-center border-r',
              dark
                ? 'border-white/[.12] text-white'
                : 'border-[#15191E]/[.08] text-[#155DFC]'
            )}
          >
            {number}
          </span>
          <span className="flex min-w-0 items-center truncate pl-3.5">
            {where}
          </span>
        </div>
      )}

      {open ? (
        <button
          type="button"
          onClick={open}
          className={cn(
            bodyClass,
            'outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#155DFC]'
          )}
        >
          {body}
        </button>
      ) : (
        <div className={bodyClass}>{body}</div>
      )}

      <Controls
        title={title}
        state={state}
        layout={layout}
        isPending={isPending}
        onToggle={onToggle}
        onOpen={open}
        onRetry={onRetry}
      />
    </article>
  );
}

function Controls({
  title,
  state,
  layout,
  isPending,
  onToggle,
  onOpen,
  onRetry,
}: {
  title: string;
  state: AppFeatureState;
  layout: AppFeatureCardLayout;
  isPending: boolean;
  onToggle: () => void;
  onOpen?: () => void;
  onRetry?: () => void;
}) {
  const { LL } = useI18nContext();
  const tile = layout === 'tile';

  let toggle: ReactNode = null;
  if (state.kind === 'loading') {
    toggle = <Skeleton data-skeleton className="h-6 w-11 rounded-full" />;
  } else if (state.kind === 'configured') {
    toggle = (
      <span className="relative flex shrink-0 items-center">
        <Switch
          className={SWITCH_HIT_AREA}
          checked={state.active}
          aria-label={LL.appHub.activate({ title })}
          aria-busy={isPending}
          onCheckedChange={() => {
            if (!isPending) onToggle();
          }}
        />
        {isPending && (
          <Loader2
            data-testid="feature-spinner"
            aria-hidden
            className="pointer-events-none absolute left-1 size-4 animate-spin text-muted-foreground"
          />
        )}
      </span>
    );
  } else if (state.kind === 'notConfigured') {
    toggle = (
      <Switch
        className={SWITCH_HIT_AREA}
        checked={false}
        aria-label={LL.appHub.activate({ title })}
        onCheckedChange={() => onOpen?.()}
      />
    );
  }

  const retry = state.kind === 'error' && (
    <Button
      variant="outline"
      size="sm"
      className="h-10 rounded-[10px] border-[#DDE1E7] px-3.5 font-semibold max-md:min-h-touch"
      onClick={onRetry}
    >
      {LL.appHub.retry()}
    </Button>
  );

  if (!tile) {
    const arrow = state.kind === 'needsIntegration' && (
      <LongArrow className="text-[#155DFC]" lineClassName="w-[18px]" />
    );
    if (!toggle && !retry && !arrow) return null;
    return (
      <span className="flex shrink-0 items-center pr-3.5">
        {toggle}
        {retry}
        {arrow}
      </span>
    );
  }

  if (!toggle && !retry && !onOpen) return null;
  return (
    <div className="mt-auto flex h-[60px] shrink-0 items-center gap-3 border-t border-[#15191E]/[.08] px-[18px]">
      {toggle}
      {retry}
      <span className="flex-1" />
      {onOpen && (
        <button
          type="button"
          onClick={onOpen}
          className="group/open flex h-11 items-center gap-3 px-0.5 text-sm font-semibold text-[#155DFC] outline-none focus-visible:ring-2 focus-visible:ring-[#155DFC] focus-visible:ring-offset-2"
        >
          {state.kind === 'needsIntegration'
            ? LL.appHub.integrate()
            : LL.appHub.configure()}
          <LongArrow lineClassName="w-[22px] group-hover:w-10 group-hover/open:w-10" />
        </button>
      )}
    </div>
  );
}
